import { useState } from "react";
import { useRouter } from "next/router";
import { ChatRuntimeProvider } from "@src/features/assistantChat/ChatRuntimeProvider";
import { useChatSidebar } from "@src/features/aiAssist/hooks/useChatSidebar";
import { transformToSelectedSession } from "@src/features/aiAssist/utils/sessionTransform";
import type { AiChatFragment } from "@src/features/assistantChat/aiChat.generated";
import {
  OutlineFlowType,
  useOutlineFlow,
} from "@src/features/contentLibrary/outlineFlows.config";
import OutlineSeedDialog from "./OutlineSeedDialog";
import OutlineCanvasDialog from "./OutlineCanvasDialog";
import { CreateAiOutlineResultFragment } from "./CreateAiOutlineResult.generated";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flowType: OutlineFlowType;
  initialInput?: string;
  onItemCreated?: (item: CreateAiOutlineResultFragment) => void;
}

const OutlineCreationFlowDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  flowType,
  initialInput,
  onItemCreated,
}) => {
  const router = useRouter();
  const [showCanvas, setShowCanvas] = useState(false);
  const config = useOutlineFlow(flowType);
  const { selectSession } = useChatSidebar(config.chatType);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setShowCanvas(false);
    onOpenChange(nextOpen);
  };

  const handleSelectHistoricalChat = (chat: AiChatFragment) => {
    // Transform and load the historical chat
    const selectedSession = transformToSelectedSession(chat);
    selectSession(selectedSession);

    // Skip to canvas since we have an existing conversation
    setShowCanvas(true);
  };

  return (
    <ChatRuntimeProvider chatType={config.chatType}>
      {/* Step 1: Prompt/options dialog */}
      <OutlineSeedDialog
        open={open && !showCanvas}
        onOpenChange={handleOpenChange}
        onStartNewChat={() => setShowCanvas(true)}
        initialInput={initialInput}
        enableChatHistory={true}
        onSelectHistoricalChat={handleSelectHistoricalChat}
        flowType={flowType}
      />

      <OutlineCanvasDialog
        dialogIsOpen={open && showCanvas}
        setDialogIsOpen={(isOpen) => {
          if (!isOpen) handleOpenChange(false);
        }}
        onItemCreated={(item: CreateAiOutlineResultFragment) => {
          if (onItemCreated) {
            onItemCreated(item);
            return;
          }
          const libraryItemId = item?.libraryItem?.id;
          if (libraryItemId) {
            router.push({
              pathname: "/library/library-item/[id]",
              query: { id: libraryItemId },
            });
          }
        }}
        flowType={flowType}
      />
    </ChatRuntimeProvider>
  );
};

export default OutlineCreationFlowDialog;
